import React from 'react';
import styled from 'styled-components';
import PadsTechSpecifics from '@components/storefront/PadsTechSpecifics';
import { CustomSliceType, PageSlice, SliceCustomSliceDetails } from '@contracts';

interface Props {
  slice: PageSlice;
}

/**
 * CustomSlice component dynamically renders a custom component based on the type specified
 * in the `slice` prop. It uses a switch statement to determine which component to render.
 *
 * Used in cases we have to add a component that is too specific or
 * complex to be a generic slice, or we are sure it will be used only
 * with one variant or ot one place, and it is not worth the efford of
 * creating a new generic slice again.
 *
 * @component
 * @param {Object} props - The component props
 * @param {PageSlice} props.slice - The slice data containing the type and other details
 * @returns {JSX.Element|null} The rendered custom component or null if the type is not specified
 *
 * @example
 * const slice = {
 *   primary: {
 *     type: CustomSliceType.PadsTechSpecifics,
 *   }
 * };
 * <CustomSlice slice={slice} />
 */
const CustomSlice: React.FC<Props> = ({ slice }) => {
  const primary = slice.primary as SliceCustomSliceDetails;

  if (!primary.type) {
    return null;
  }

  const renderCustomComponentByType = (type: string) => {
    switch (type) {
      case CustomSliceType.PadsTechSpecifics:
        return <PadsTechSpecifics />;
      default:
        return null;
    }
  };

  return <Container>{renderCustomComponentByType(primary.type)}</Container>;
};

export default CustomSlice;

const Container = styled.div`
  width: 100%;
`;
